import React, { useState, useMemo, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useSelector } from 'hooks/redux'
import PropTypes from 'prop-types'
import strings from 'utils/strings'
import { map, isEmpty, compose, reject, equals } from 'ramda'

import { resetHarnesses } from 'actions/feature/harnesses'
import { updateXmcModuleName } from 'actions/feature/featureMetadata'
import { Paragraph } from 'components/patterns/Typography'
import { getAuthenticatedModulesForCreation } from 'selectors/authorization/checPlus'
import { RadioButtonGroup } from 'components/patterns'
import { displayModuleName } from 'i18n/displayConstants'
import { ASAM, BUS_MODULES, NON_BUS_MODULES } from 'utils/constants'
import { ConfirmIcon, ConfirmTitle } from './FeatureEditor.style'

const isValidBusModule = (module) => BUS_MODULES.includes(module)
const isValidNonBusModule = (module) => NON_BUS_MODULES.includes(module)

const formatModuleOptions = (options, isInitial, busMode) => {
  const format = map((moduleName) => ({
    text: moduleName,
    label: displayModuleName(moduleName),
    value: moduleName,
    key: moduleName,
    disabled: busMode
      ? !isValidBusModule(moduleName)
      : !isValidNonBusModule(moduleName),
  }))
  const transform = isInitial ? format : compose(format, reject(equals(ASAM)))
  return transform(options)
}

const popConfirmTitle = (
  <ConfirmTitle>
    <Paragraph size="medium">{strings.change_ecu_subheading}</Paragraph>
  </ConfirmTitle>
)

const ModuleNameRadioEditor = ({
  moduleNameIsEditable,
  moduleName,
  isInitial,
  busMode,
  featureId,
  harnessesMapped,
}) => {
  const dispatch = useDispatch()
  const [ecuToChange, setEcuToChange] = useState(null)
  const creatableModules = useSelector(getAuthenticatedModulesForCreation)
  const moduleOptions = useMemo(
    () => formatModuleOptions(creatableModules, isInitial, busMode),
    [busMode, creatableModules, isInitial]
  )

  useEffect(() => {
    // determind valid ecu modules per mode
    const validModules = creatableModules.filter(
      busMode ? isValidBusModule : isValidNonBusModule
    )
    // pick first one as a default
    const defaultModule = validModules[0]
    // if user already picked default, do nothing
    if (moduleName === defaultModule) return
    // per mode ensure default if selected module is invalid
    if (busMode && !validModules.includes(moduleName)) {
      dispatch(updateXmcModuleName(defaultModule, featureId))
    } else if (!busMode && !validModules.includes(moduleName)) {
      dispatch(updateXmcModuleName(defaultModule, featureId))
    }
  }, [dispatch, featureId, moduleName, creatableModules, busMode])

  const handleECUChange = (event) => {
    if (isEmpty(harnessesMapped)) {
      dispatch(updateXmcModuleName(event.target.value, featureId))
      setEcuToChange(null)
    } else {
      setEcuToChange(event.target.value)
    }
  }

  // pop confirm when harnesses mapped props
  function handleConfirmECUChange() {
    if (!isEmpty(harnessesMapped) && ecuToChange !== null) {
      setEcuToChange(null)
      dispatch(resetHarnesses())
      dispatch(updateXmcModuleName(ecuToChange, featureId))
    }
  }

  const popConfirmProps = {
    showConfirm: !isEmpty(harnessesMapped),
    title: popConfirmTitle,
    okText: strings.change_ecu_and_harnesses,
    okType: 'danger',
    cancelType: 'primary',
    cancelText: strings.keep_ecu_and_harnesses,
    onConfirm: handleConfirmECUChange,
    placement: 'topLeft',
    icon: (
      <ConfirmIcon
        icon="alert_exclamation_triangle_solid"
        fill="alertPrimary"
      />
    ),
  }

  if (!moduleNameIsEditable) return displayModuleName(moduleName)

  return (
    <RadioButtonGroup
      label={strings.ecu}
      currentValue={moduleName}
      buttons={moduleOptions}
      onChange={handleECUChange}
      className="module-name-selector"
      popConfirm={{ ...popConfirmProps }}
      vertical
      required
    />
  )
}

ModuleNameRadioEditor.propTypes = {
  moduleNameIsEditable: PropTypes.bool,
  updateModuleName: PropTypes.func,
  moduleName: PropTypes.string,
  size: PropTypes.string,
  isInitial: PropTypes.bool,
  featureId: PropTypes.string,
  busMode: PropTypes.bool,
  popConfirmProps: PropTypes.object,
  ecuToChange: PropTypes.string,
  setEcuToChange: PropTypes.func,
  harnessesMapped: PropTypes.array,
}

export default ModuleNameRadioEditor
